//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import CloseIcon from '@mui/icons-material/Close'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import React, { ReactElement, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { CompanyProfileCTO, OPEN_TRADE_UUID } from '@shared/CompanyProfileCTO'

import { ReportDefinitionCTO } from '@shared/ReportDefinitionCTO'

import { modalStyle } from '@react/components/ButtonModal'
import { reportDefinitionPut, reportDefinitionsForShape } from '@react/fetchers/reportDefinitionFetchers'
import { useOtChainState } from '@react/redux/store'
import { reportColumnsForDescriptor } from '@react/reporting/ReportingColumns'
import { ReportDataShape } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'

import { ReportDescriptor, ReportSelectorType } from './ReportDescriptors'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

export default function ReportExplorer(props: {
  loading: boolean
  loadingProgress: string | undefined
  reportDescriptor: ReportDescriptor
  rows: GridRowsProp
  summaryRows?: GridRowsProp
}): React.ReactElement {
  const { rows, reportDescriptor, loadingProgress } = props

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()

  const [columns, setColumns] = useState<GridColDef[]>([])
  const companyProfile: CompanyProfileCTO = {
    uuid: OPEN_TRADE_UUID,
    companyName: 'Open Trade',
    wallets: [],
    emails: [],
  }

  useEffect(() => {
    const columnVisibilityString = localStorage.getItem('reportColumnVisibilityModel') as string | undefined
    if (columns && columnVisibilityString) {
      const columnVisibilityModel = JSON.parse(columnVisibilityString)
      setColumnVisibilityModel(columnVisibilityModel)
    }
  }, [columns])

  const [reportDefinition, setReportDefinition] = useState<ReportDefinitionCTO | undefined>(undefined)
  const [reportDefinitions, setReportDefinitions] = useState<ReportDefinitionCTO[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean> | undefined>(undefined)
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(undefined)

  const handleSetColumnVisibilityModel = (newModel: Record<string, boolean> | undefined) => {
    if (newModel) {
      const str = JSON.stringify(newModel)

      localStorage.setItem('reportColumnVisibilityModel', str)
    }
    setColumnVisibilityModel(newModel)
  }

  useEffect(() => {
    const asyncFunction = async () => {
      if (serverApiBaseUrl && restAPIKey && reportDescriptor) {
        const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
          serverApiBaseUrl,
          restAPIKey,
          companyProfile?.uuid,
          reportDescriptor
        )

        console.log('Set report definitions', _reportDefinitions)

        setReportDefinitions(_reportDefinitions)
        if (_reportDefinitions.length > 0) {
          setReportDefinition(_reportDefinitions[0])
        }
      }
    }
    asyncFunction().catch((e) => {
      logOT.error('Error getting report definitions', e)
    })
  }, [serverApiBaseUrl, restAPIKey, reportDescriptor])

  useEffect(() => {
    const { columns: _columns, columnVisibilityModel: _columnVisibilityModel } =
      reportColumnsForDescriptor(reportDescriptor)

    const timestampColumn = _columns.find((col) => col.field.endsWith('timestamp'))
    if (timestampColumn) {
      const timestampSortModel: GridSortModel = [{ field: timestampColumn.field, sort: 'desc' }]
      setSortModel(timestampSortModel)
    }

    setColumns(_columns)
    handleSetColumnVisibilityModel(_columnVisibilityModel)
  }, [reportDescriptor])

  function CustomToolbar(): ReactElement {
    const [openModal, setOpenModal] = React.useState(false)
    const [reportName, setReportName] = useState<string>('')

    const theme = useTheme()
    useEffect(() => {
      window.addEventListener(
        'modalClose',
        () => {
          setOpenModal(false)
        },
        false
      )

      return () => {
        window.removeEventListener(
          'modalClose',
          () => {
            setOpenModal(false)
          },
          false
        )
      }
    }, [])

    const apiRef = useGridApiContext()
    const handleReportChange = (event: SelectChangeEvent) => {
      const asyncFunction = async () => {
        const reportUuid = event.target.value as string

        const _reportDefinition = reportDefinitions.find((r) => r.reportUuid === reportUuid)
        setReportDefinition(_reportDefinition)

        if (_reportDefinition?.xGridDefinition) {
          apiRef.current.restoreState(_reportDefinition.xGridDefinition)
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error getting columns', e)
      })
    }
    const handleSaveReport = () => {
      const asyncFunction = async () => {
        const state = apiRef.current.exportState()
        const newReportDefinition = {
          ...reportDefinition,
          xGridDefinition: state,
        }

        if (serverApiBaseUrl) {
          await reportDefinitionPut(serverApiBaseUrl, restAPIKey, newReportDefinition).catch((e: any) => {
            logOT.error('Error saving report', e)
          })
          const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
            serverApiBaseUrl,
            restAPIKey,
            companyProfile?.uuid,
            reportDescriptor
          )

          setReportDefinitions(_reportDefinitions)
          handleClose()
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error saving report', e)
      })
    }
    const handleSaveNewReport = () => {
      const asyncFunction = async () => {
        const state = apiRef.current.exportState()

        const newReportDefinition: ReportDefinitionCTO = {
          isGlobal: false,
          reportName: reportName,
          reportUuid: uuidv4(),
          timestamp: Date.now(),
          dataShape: reportDefinition?.dataShape ?? ReportDataShape.UNKNOWN,
          defaultForShape: false,
          companyUuid: OPEN_TRADE_UUID,
          xGridDefinition: state,
        }

        if (serverApiBaseUrl) {
          await reportDefinitionPut(serverApiBaseUrl, restAPIKey, newReportDefinition).catch((e: any) => {
            logOT.error('Error saving report', e)
          })
          const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
            serverApiBaseUrl,
            restAPIKey,
            companyProfile?.uuid,
            reportDescriptor
          )
          setReportDefinitions(_reportDefinitions)
          handleClose()
        }
      }
      asyncFunction().catch((e) => {
        logOT.error('Error saving report', e)
      })
    }
    const handleClose = (): void => {
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    }

    return (
      <>
        <GridToolbarContainer
          data-testid="GridToolbarContainer1"
          style={{ float: 'right', padding: '15px' }}
        ></GridToolbarContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 2,
              alignItems: 'center',
              padding: '0 15px 10px 17px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {reportDescriptor.selectors[ReportSelectorType.REPORT_DEFINITION] && (
                <Box sx={{ width: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel id="select-report">Report</InputLabel>
                    <Select
                      labelId="select-report"
                      id="select-report"
                      value={reportDefinition?.reportUuid ?? ''}
                      label="Report"
                      onChange={handleReportChange}
                    >
                      {reportDefinitions.map((r) => (
                        <MenuItem key={r.reportUuid} value={r.reportUuid}>
                          {r.reportName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {reportDescriptor.selectors[ReportSelectorType.REPORT_DEFINITION] && (
                <Button
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    color: '#171717',
                    backgroundColor: '#EEEEEE',
                    '&:hover': {
                      backgroundColor: '#E0E0E0',
                    },
                  }}
                  onClick={() => {
                    setReportName('')
                    setOpenModal(true)
                  }}
                >
                  Save Report Definition
                </Button>
              )}
              <GridToolbarFilterButton
                slotProps={
                  {
                    button: {
                      startIcon: <FilterAltOutlinedIcon />,
                      style: {
                        height: '36px',
                        color: '#171717',
                        textTransform: 'none',
                        backgroundColor: '#EEEEEE',
                        padding: '0 12px',
                      },
                    },
                  } as any
                }
              />
              <GridToolbarColumnsButton
                slotProps={{
                  button: {
                    style: {
                      height: '36px',
                      color: '#171717',
                      textTransform: 'none',
                      backgroundColor: '#EEEEEE',
                      padding: '0 12px',
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GridToolbarExport
                excelOptions={{}}
                csvOptions={{}}
                slotProps={{
                  button: {
                    style: {
                      height: '36px',
                      color: '#171717',
                      textTransform: 'none',
                      backgroundColor: '#EEEEEE',
                      padding: '0 12px',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {props.loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 0',
              }}
            >
              <Box>
                <CircularProgress />
              </Box>
              <Typography textAlign={'center'} variant="bodyMuted" sx={{ mt: 1 }}>
                Loading Report Data
              </Typography>
              {loadingProgress && (
                <Typography textAlign={'center'} variant="bodyMuted" sx={{ mt: 0.5 }}>
                  {loadingProgress}
                </Typography>
              )}
            </Box>
          )}

          <Modal
            open={openModal}
            data-testid="modal"
            onClose={() => setOpenModal(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Paper sx={modalStyle}>
              <Grid alignItems={'stretch'} direction={'row'} container spacing={8} sx={{ flexGrow: 1 }}>
                <Grid key="1" item xs={12}>
                  <Typography
                    align="left"
                    sx={{ display: 'inline', float: 'left', fontWeight: 'bold', marginLeft: 2 }}
                    variant={'h4'}
                  >
                    Save Report Definition
                  </Typography>

                  <IconButton
                    onClick={handleClose}
                    sx={{
                      height: '14px',
                      width: '14px',
                      padding: 0,
                      float: 'right',
                      color: theme.palette.primary.main,
                    }}
                  >
                    <CloseIcon sx={{ width: '12px', height: '12px', marginTop: 0, color: theme.palette.fg.muted }} />
                  </IconButton>
                </Grid>
                <Grid key="2" item xs={6}>
                  <Typography>{reportDefinition?.reportName}</Typography>
                </Grid>
                <Grid key="3" item xs={6}>
                  <Button variant="contained" onClick={handleSaveReport}>
                    Save
                  </Button>
                </Grid>
                <Grid key="4" item xs={6}>
                  <TextField
                    id="outlined-controlled"
                    label="New Report Name"
                    value={reportName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setReportName(event.target.value)
                    }}
                  />
                </Grid>
                <Grid key="5" item xs={6}>
                  <Button disabled={reportName.length === 0} variant="contained" onClick={handleSaveNewReport}>
                    Save new
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Modal>
        </Box>
      </>
    )
  }

  const modifiedRows = rows?.map((row) => ({
    ...row,
    assets: isNaN(row?.assets) ? 0 : row.assets,
  }))

  const sumErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))
        .toString()

      return sum
    },
    label: 'Sum ERC20',
    columnTypes: ['erc20'],
  }
  const aveErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))

      const ave: bigint = vals.length > 0 ? sum / BigInt(vals.length) : BigInt(0)
      return ave.toString()
    },
    label: 'Ave ERC20',
    columnTypes: ['erc20'],
  }

  if (reportDefinition?.xGridDefinition && columns.length > 0) {
    return (
      <DataGridPremium
        rows={modifiedRows}
        columns={columns}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleSetColumnVisibilityModel}
        onSortModelChange={(newModel) => setSortModel(newModel)}
        initialState={reportDefinition.xGridDefinition}
        sx={{ fontSize: 12, fontWeight: 500 }}
        autoHeight
        slotProps={{ toolbar: { csvOptions: { allColumns: true } } }}
        slots={{
          toolbar: CustomToolbar,
          openFilterButtonIcon: FilterAltOutlinedIcon,
        }}
        getDetailPanelHeight={() => 'auto'}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          sumErc20: sumErc20Aggregation,
          aveErc20: aveErc20Aggregation,
        }}
      />
    )
  }
  if (columns.length > 0) {
    return (
      <DataGridPremium
        rows={modifiedRows}
        columns={columns}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleSetColumnVisibilityModel}
        onSortModelChange={(newModel) => setSortModel(newModel)}
        sx={{ fontSize: 12, fontWeight: 500 }}
        autoHeight
        slots={{
          toolbar: CustomToolbar,
          openFilterButtonIcon: FilterAltOutlinedIcon,
        }}
        getDetailPanelHeight={() => 'auto'}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          sumErc20: sumErc20Aggregation,
          aveErc20: aveErc20Aggregation,
        }}
      />
    )
  }
  return <div>Loading...</div>
}
